import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, Typography, Button, Chip, CircularProgress, useTheme } from '@mui/material';
import ConfirmDisconnectModal from '../common/ConfirmDisconnectModal';
import { Agent } from '../../types/agent';
import { agentApi } from '../../utils/agentApi';
import ZAPIConfigModal from '../admin/ZAPIConfigModal';
import ZAPIConnectionModal from '../admin/ZAPIConnectionModal';
import WhatsAppConfigModal from '../admin/WhatsAppConfigModal';
import { WhatsAppConfig } from '../../utils/agentApi';
import { useTranslation } from 'react-i18next';

interface ZAPICardProps {
  agentId: string;
  agent?: Agent | null;
  onAgentUpdate?: (agent: Agent) => void;
  isWhatsAppNativeConnected?: boolean;
  isWhatsAppOficialConnected?: boolean;
  onConnectZApi?: () => Promise<void>;
  onDisconnectZApi?: () => Promise<void>;
}

const ZAPICard: React.FC<ZAPICardProps> = ({
  agentId,
  agent,
  onAgentUpdate,
  isWhatsAppNativeConnected = false,
  isWhatsAppOficialConnected = false,
  onConnectZApi,
  onDisconnectZApi
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [whatsappConfigModalOpen, setWhatsappConfigModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [zapiStatus, setZapiStatus] = useState<{
    connected: boolean;
    session: boolean;
    smartphoneConnected: boolean;
    error?: string;
    phoneNumber?: string;
  } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isConnected = zapiStatus?.connected || false;
  const hasConfig = agent?.zApiConfig && agent.zApiConfig.clientToken && agent.zApiConfig.instanceId && agent.zApiConfig.instanceToken;

  const hasWhatsAppOficialConfig = !!agent?.whatsappOficialConfig;
  const isDisabled = isWhatsAppNativeConnected || isWhatsAppOficialConnected || hasWhatsAppOficialConfig;

  const checkZApiStatus = useCallback(async () => {
    const hasConfig = agent?.zApiConfig && agent.zApiConfig.clientToken && agent.zApiConfig.instanceId && agent.zApiConfig.instanceToken;
    if (!hasConfig) return;

    setIsCheckingStatus(true);
    try {
      const status = await agentApi.getZApiStatus(agentId);
      setZapiStatus(status);
    } catch (error) {
      console.error('Erro ao verificar status Z-API:', error);
    } finally {
      setIsCheckingStatus(false);
    }
  }, [agent, agentId]);

  useEffect(() => {
    if (hasConfig) {
      checkZApiStatus();
    }
  }, [hasConfig, checkZApiStatus]);

  const handleConfigureClick = () => {
    setConfigModalOpen(true);
  };

  const handleConfigSave = async (config: { clientToken: string; instanceId: string; instanceToken: string }) => {
    if (!agent) return;

    setIsLoading(true);
    try {
      const updatedAgent = await agentApi.updateZApiConfig(agentId, config);

      if (onAgentUpdate) {
        onAgentUpdate(updatedAgent);
      }
      setConfigModalOpen(false);
      setConnectionModalOpen(true);
    } catch (error) {
      console.error('Erro ao salvar configuração Z-API:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectionSuccess = async () => {
    setConnectionModalOpen(false);

    // Se Z-API já está configurada, forçar atualização no backend
    if (agent?.zApiConfig) {
      const updatedAgent = await agentApi.updateZApiConfig(agentId, {
        clientToken: agent.zApiConfig.clientToken,
        instanceId: agent.zApiConfig.instanceId,
        instanceToken: agent.zApiConfig.instanceToken
      });

      if (onAgentUpdate) {
        onAgentUpdate(updatedAgent);
      }
    }

    // Usar a função do hook para conectar Z-API
    if (onConnectZApi) {
      await onConnectZApi();
    }

    await checkZApiStatus();
  };

  const handleDisconnect = async () => {
    // Só desconectar se realmente estiver conectado
    if (!isConnected) {
      console.log('Z-API não está conectada, não é necessário desconectar');
      return;
    }

    // Usar a função do hook para desconectar Z-API
    if (onDisconnectZApi) {
      await onDisconnectZApi();
    }

    setZapiStatus(null);
  };

  const handleWhatsAppConfig = () => {
    setWhatsappConfigModalOpen(true);
  };

  const handleWhatsAppConfigSave = async (config: WhatsAppConfig) => {
    if (!agent) return;

    setIsLoading(true);
    try {
      const updatedAgent = await agentApi.updateWhatsAppConfig(agentId, config);
      if (onAgentUpdate) {
        onAgentUpdate(updatedAgent);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card sx={{
        maxWidth: 720,
        opacity: isDisabled ? 0.6 : 1,
        filter: isDisabled ? 'grayscale(0.3)' : 'none',
        '&:hover': {
          opacity: isDisabled ? 0.7 : 1,
        }
      }}>
        <CardContent>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ color: isConnected ? '#22c55e' : '#9ca3af' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>
              <div>
                <Typography variant="h6" sx={{ fontWeight: 600, color: isDisabled ? 'text.disabled' : 'inherit' }}>
                  {t('agents.zapi.title')}
                </Typography>
                <Typography variant="body2" color={isDisabled ? 'text.disabled' : 'text.secondary'}>
                  {isDisabled 
                    ? (isWhatsAppOficialConnected ? t('agents.whatsapp.disabledWhatsAppOfficial') : t('agents.zapi.disabledWhatsApp'))
                    : isConnected 
                      ? t('agents.zapi.connectedWith', { phone: zapiStatus?.phoneNumber || t('agents.zapi.phoneNumberNotAvailable') })
                      : hasConfig 
                        ? t('agents.zapi.configured')
                        : t('agents.zapi.notConfigured')}
                </Typography>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isCheckingStatus && <CircularProgress size={16} />}
              <Chip
                label={isDisabled ? t('agents.zapi.disabled') : isConnected ? t('agents.zapi.connected') : t('agents.zapi.disconnected')}
                color={isDisabled ? 'default' : isConnected ? 'success' as any : 'default'}
                size="small"
                sx={isDisabled ? {
                  backgroundColor: 'grey.300',
                  color: 'grey.600'
                } : {
                  color: theme.palette.mode === 'dark' ? '#000' : undefined
                }}
              />
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isDisabled ? (
              <Button
                variant="contained"
                disabled
                sx={{
                  backgroundColor: 'grey.300',
                  color: 'grey.500',
                  '&:hover': {
                    backgroundColor: 'grey.300',
                  }
                }}
              >
                {isWhatsAppOficialConnected ? t('agents.whatsapp.whatsappConnected') : t('agents.whatsapp.whatsappConnected')}
              </Button>
            ) : isConnected ? (
              <>
                <Button variant="outlined"
                  color="error" onClick={() => setConfirmOpen(true)} disabled={isLoading}>
                  {isLoading ? t('agents.zapi.disconnecting') : t('agents.zapi.disconnect')}
                </Button>
                <Button variant="outlined" onClick={handleWhatsAppConfig} disabled={isLoading}>
                  {t('agents.zapi.configure')}
                </Button>
              </>
            ) : hasConfig ? (
              <Button
                variant="contained"
                color="primary"
                sx={{
                  color: theme.palette.mode === 'dark' ? '#000' : undefined
                }}
                onClick={() => setConnectionModalOpen(true)}
                disabled={isLoading}
              >
                {t('agents.zapi.connect')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                sx={{
                  color: theme.palette.mode === 'dark' ? '#000' : undefined
                }}
                onClick={handleConfigureClick}
                disabled={isLoading}
              >
                {t('agents.zapi.configure')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <ZAPIConfigModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        agent={agent || null}
        onSave={handleConfigSave}
        isLoading={isLoading}
      />

      <ZAPIConnectionModal
        open={connectionModalOpen}
        onClose={() => setConnectionModalOpen(false)}
        agent={agent || null}
        onConnectionSuccess={handleConnectionSuccess}
        onConfigUpdate={handleConfigSave}
      />

      <WhatsAppConfigModal
        open={whatsappConfigModalOpen}
        onClose={() => setWhatsappConfigModalOpen(false)}
        agent={agent || null}
        onSave={handleWhatsAppConfigSave}
        isLoading={isLoading}
      />

      <ConfirmDisconnectModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          setConfirmOpen(false);
          await handleDisconnect();
        }}
        isLoading={isLoading}
        description={t('agents.zapi.disconnectWarning')}
      />
    </>
  );
};

export default ZAPICard;
